import { Injectable } from '@angular/core'
import type {
    AcademicCalendarBackdatedSessionPlansConfig,
    RuntimeConfig,
} from 'src/environments/environment.type'

interface LegacyAppConfig {
    ApiEndPoints?: string
    ApiEndPoint?: string
    EiNavigatorURL?: string
    modal?: boolean
    beta?: boolean
    secreteKey?: string
    authToken?: string
}

type AppRuntimeConfig = LegacyAppConfig & RuntimeConfig

const DEFAULT_APP_CONFIG: AppRuntimeConfig = {
    academicCalendar: {
        backdatedSessionPlans: {
            enabled: false,
            allowedPastDays: 0,
        },
    },
}

@Injectable({
    providedIn: 'root',
})
export class AppConfigService {
    private _appConfig: AppRuntimeConfig = DEFAULT_APP_CONFIG

    async load(): Promise<void> {
        try {
            const response = await fetch('/assets/json/app.config.json', {
                cache: 'no-store',
            })
            if (!response.ok) {
                return
            }
            const config = (await response.json()) as AppRuntimeConfig
            this._appConfig = {
                ...DEFAULT_APP_CONFIG,
                ...config,
                academicCalendar: {
                    ...DEFAULT_APP_CONFIG.academicCalendar,
                    ...config.academicCalendar,
                    backdatedSessionPlans: {
                        enabled:
                            config.academicCalendar?.backdatedSessionPlans?.enabled ??
                            DEFAULT_APP_CONFIG.academicCalendar?.backdatedSessionPlans?.enabled ??
                            false,
                        allowedPastDays:
                            config.academicCalendar?.backdatedSessionPlans?.allowedPastDays ??
                            DEFAULT_APP_CONFIG.academicCalendar?.backdatedSessionPlans
                                ?.allowedPastDays ??
                            0,
                    },
                },
                features: {
                    ...DEFAULT_APP_CONFIG.features,
                    ...config.features,
                },
                security: {
                    ...DEFAULT_APP_CONFIG.security,
                    ...config.security,
                    trustedTypes: {
                        ...DEFAULT_APP_CONFIG.security?.trustedTypes,
                        ...config.security?.trustedTypes,
                    },
                },
            }
        } catch (error) {
            console.error('Failed to load app config:', error)
        }
    }

    // Returns the value for a given configuration key
    getConfigValue<TKey extends keyof AppRuntimeConfig>(key: TKey): AppRuntimeConfig[TKey] {
        return this._appConfig[key]
    }

    getAcademicCalendarBackdatedSessionPlansConfig(): AcademicCalendarBackdatedSessionPlansConfig {
        const config = this._appConfig.academicCalendar?.backdatedSessionPlans
        return {
            enabled: config?.enabled === true,
            allowedPastDays: Math.max(0, Math.floor(config?.allowedPastDays ?? 0)),
        }
    }
}
